/*
 * FOR SALE — cartes d'annonce de l'accueil (direction « Hangar », snippet fs-listing-card).
 *
 * La carte n'a besoin d'aucun script pour fonctionner : « Voir l'annonce » est un lien vers la page de l'annonce
 * (/pages/listings/<handle>), étiré sur toute la carte. Ce script n'active que le rail de la flotte (snippet
 * fs-listing-bay : groupe nommé de boutons, un par vaisseau ou design de drones) :
 *  - clic ou toucher sur une vignette : elle passe en vedette et y reste (aria-pressed), sans quitter la liste : images
 *    .fs-bay-hero et .fs-bay-refl (même fichier, src, srcset et --ship-w recopiés de la vignette), légende
 *    [data-fs-bay-name] et rareté [data-fs-bay-tier] (crans + mot, jamais la couleur seule) ; fondu bref (.is-swapping,
 *    140 ms), aucun en mouvement réduit ;
 *  - au pointeur fin (souris), le survol d'une vignette la prévisualise ; quitter la carte revient au vaisseau choisi ;
 *    seul un vrai mouvement du pointeur compte : la page qui défile sous une souris immobile ne change aucun vaisseau ;
 *  - clavier : un seul arrêt de tabulation pour le rail (tabulation itinérante, sur le vaisseau choisi), flèches gauche
 *    et droite, Début et Fin pour aller d'une vignette à l'autre, Entrée ou Espace pour la choisir : 3 arrêts par carte
 *    à rail (Comparer, rail, Voir l'annonce), 2 sans rail.
 * « +N », le séparateur et les espaces entre vignettes laissent passer le clic vers le lien de la carte (fs-listings.css) :
 * ils mènent à la page de l'annonce, comme le reste de la carte (Ctrl, Cmd ou clic milieu : nouvel onglet).
 * Sans script, les vignettes restent inertes (disabled, rail aria-hidden et traversé par le pointeur) : aucune zone
 * morte. La racine [data-fs-demo] porte data-fs-cards-ready quand le rail est actif. Aucune requête réseau, aucun texte
 * d'annonce inséré en HTML, zone [data-fs-commerce] jamais touchée.
 */
(() => {
  if (window.FSListingCards) return;
  const fine = typeof matchMedia === 'function' ? matchMedia('(hover: hover) and (pointer: fine)') : { matches: false };
  const reducedMotion = () => document.documentElement.dataset.fsMotion === 'reduced'
    || (typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches);
  const { t } = window.fsI18n;
  const TIERS = { rarest: ['rare', t('listing.tier.rare'), 3], paid: ['premium', t('listing.tier.premium'), 2] };

  /** Pastille de rareté, même balisage que le snippet fs-listing-tier (crans + mot). */
  const tierChip = (tier) => {
    const [name, word, on] = TIERS[tier];
    const chip = document.createElement('span');
    chip.className = `fs-tier fs-tier--${name}`;
    chip.dataset.tier = tier;
    const pips = document.createElement('span');
    pips.className = `fs-tier-pips fs-tier-pips--${name}`;
    pips.setAttribute('aria-hidden', 'true');
    for (let i = 1; i <= 3; i += 1) { const pip = document.createElement('b'); if (i > on) pip.className = 'is-off'; pips.append(pip); }
    chip.append(pips, word);
    return chip;
  };

  /** Vedette de la baie d'une carte : la vignette donnée (son image, son nom, sa rareté). */
  const feature = (card, slot) => {
    const ship = card.querySelector('[data-fs-bay-ship]');
    const thumb = slot?.querySelector('img');
    if (!ship || !thumb) return;
    clearTimeout(Number(ship.dataset.fsSwapTimer || 0));
    if (slot.classList.contains('is-hero')) { ship.classList.remove('is-swapping'); return; }
    const apply = () => {
      ship.querySelectorAll('.fs-bay-hero, .fs-bay-refl').forEach((img) => {
        const srcset = thumb.getAttribute('srcset');
        if (srcset) img.setAttribute('srcset', srcset); else img.removeAttribute('srcset');
        img.src = thumb.currentSrc || thumb.src;
        ['width', 'height'].forEach((name) => { if (thumb.hasAttribute(name)) img.setAttribute(name, thumb.getAttribute(name)); });
        // Plafonds nets (--ship-w, --ship-w1 : fs-bay.css) de la vignette (design de drones : portés par le bouton).
        for (const name of ['--ship-w', '--ship-w1']) {
          const width = thumb.style.getPropertyValue(name) || slot.style.getPropertyValue(name);
          if (width) img.style.setProperty(name, width); else img.style.removeProperty(name);
        }
      });
      const name = card.querySelector('[data-fs-bay-name]');
      if (name) name.textContent = slot.dataset.fsBayLabel || '';
      const tier = card.querySelector('[data-fs-bay-tier]');
      if (tier) {
        const value = slot.dataset.tier || '';
        tier.replaceChildren(...(TIERS[value] ? [tierChip(value)] : []));
        tier.hidden = !TIERS[value];
      }
      card.querySelectorAll('.fs-bay-slot.is-hero').forEach((item) => item.classList.remove('is-hero'));
      slot.classList.add('is-hero');
      ship.classList.remove('is-swapping');
    };
    if (reducedMotion()) { apply(); return; }
    ship.classList.add('is-swapping');
    ship.dataset.fsSwapTimer = String(setTimeout(apply, 140));
  };

  /** Choix d'une vignette : vedette, aria-pressed et arrêt de tabulation du rail. */
  const choose = (card, slot) => {
    card.querySelectorAll('.fs-bay-rail button[data-fs-bay-index]').forEach((item) => {
      const on = item === slot;
      item.setAttribute('aria-pressed', String(on));
      item.tabIndex = on ? 0 : -1;
    });
    feature(card, slot);
  };

  /* Survol réel seulement. Quand la page défile sous un pointeur immobile, le navigateur fait entrer le pointeur dans
     chaque vignette qui passe dessous (pointerover sans mouvement) : la vedette changerait sous une souris qui ne bouge
     pas, avec téléchargement, décodage et recalcul des styles en plein défilement (à-coups sous Lenis). L'aperçu ne
     suit donc qu'un pointermove qui déplace vraiment le pointeur, et jamais pendant un défilement ni dans les SETTLE ms
     qui suivent. */
  const SETTLE = 120;
  let scrolledAt = -Infinity;
  let lastPoint = null;
  document.addEventListener('scroll', () => { scrolledAt = performance.now(); }, { capture: true, passive: true });
  /* Dernière position connue (bouillonnement : le rail lit encore la précédente). */
  document.addEventListener('pointermove', (event) => { lastPoint = [event.clientX, event.clientY]; }, { passive: true });
  const realMove = (event) => Boolean(event.movementX || event.movementY)
    || !lastPoint || event.clientX !== lastPoint[0] || event.clientY !== lastPoint[1];

  const boot = (root) => {
    if (root.hasAttribute('data-fs-cards-ready')) return;
    const cards = [...root.querySelectorAll('.fs-lc--hangar[data-fs-listing]')];
    if (!cards.length) return;
    cards.forEach((card) => {
      const rail = card.querySelector('.fs-bay-rail');
      const slots = rail ? [...rail.querySelectorAll('button[data-fs-bay-index]')] : [];
      if (!slots.length) return;
      const chosen = () => slots.find((slot) => slot.getAttribute('aria-pressed') === 'true') || slots[0];
      slots.forEach((slot, index) => {
        slot.disabled = false;
        slot.setAttribute('aria-pressed', String(index === 0));
        slot.tabIndex = index === 0 ? 0 : -1;
      });
      rail.removeAttribute('aria-hidden');
      rail.addEventListener('click', (event) => {
        const slot = event.target.closest?.('button[data-fs-bay-index]');
        if (slot && rail.contains(slot)) choose(card, slot);
      });
      /* Aperçu à l'entrée d'une vignette, comme un survol : une fois par vignette tant que le pointeur y reste. */
      let hovered = null;
      rail.addEventListener('pointermove', (event) => {
        if (!fine.matches || event.pointerType === 'touch') return;
        if (!realMove(event) || performance.now() - scrolledAt < SETTLE) return;
        const found = event.target.closest?.('button[data-fs-bay-index]');
        const slot = found && rail.contains(found) ? found : null;
        if (slot === hovered) return;
        hovered = slot;
        if (slot) feature(card, slot);
      }, { passive: true });
      /* Le pointeur quitte la carte : le vaisseau choisi revient (aperçu au survol terminé). */
      card.addEventListener('pointerleave', () => { hovered = null; feature(card, chosen()); });
      /* Tabulation itinérante : flèches, Début, Fin (sans boucle) ; le choix se fait par Entrée ou Espace. */
      rail.addEventListener('keydown', (event) => {
        const at = slots.indexOf(event.target);
        if (at < 0 || event.altKey || event.ctrlKey || event.metaKey) return;
        const to = { ArrowLeft: at - 1, ArrowRight: at + 1, Home: 0, End: slots.length - 1 }[event.key];
        if (to === undefined) return;
        event.preventDefault();
        const next = slots[Math.max(0, Math.min(slots.length - 1, to))];
        slots.forEach((slot) => { slot.tabIndex = slot === next ? 0 : -1; });
        next.focus();
      });
      /* Sortie du rail au clavier : l'arrêt de tabulation revient au vaisseau choisi. */
      rail.addEventListener('focusout', (event) => {
        if (rail.contains(event.relatedTarget)) return;
        const current = chosen();
        slots.forEach((slot) => { slot.tabIndex = slot === current ? 0 : -1; });
      });
    });
    root.setAttribute('data-fs-cards-ready', '');
  };

  const start = () => document.querySelectorAll('[data-fs-demo][data-fs-source="listings"]').forEach((root) => {
    try { boot(root); } catch (error) { console.warn('[Space Syndicate] aperçu de la flotte indisponible', error); }
  });
  window.FSListingCards = Object.freeze({ feature, choose });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
  document.addEventListener('shopify:section:load', start);
})();
